import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import AccountTabs from './components/AccountTabs';
import StrategyGrid from './components/StrategyGrid';
import SymbolListPanel from './components/SymbolListPanel';
import CopyTargets from './components/CopyTargets';
import CopyTradeMonitor from './components/CopyTradeMonitor';
import PopupOverlay from './components/PopupOverlay';
import type { Strategy } from '@/types/strategy';

export interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface GatewayStatus {
  connected: boolean;
  accounts: CtraderAccount[];
}

type PopupType = 'copyTrade' | null;

export default function Strategies() {
  const [gatewayConnected, setGatewayConnected] = useState(false);
  const [gatewayAccounts, setGatewayAccounts] = useState<CtraderAccount[]>([]);
  const gatewayPolledRef = useRef(false);

  // Account selection — restore from URL query (e.g. returning from a monitor page)
  const [searchParams] = useSearchParams();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(searchParams.get('accountId') || null);

  // Strategies
  const [loadedStrategies, setLoadedStrategies] = useState<Strategy[]>([]);

  // MT4 accounts from user_connections (Dashboard-configured master MT4 accounts)
  const [userConnectionMt4s, setUserConnectionMt4s] = useState<Array<{ account_id: string; account_name: string; is_live: boolean }>>([]);

  // Popup state
  const [activePopup, setActivePopup] = useState<PopupType>(null);

  // ─── Fetch gateway status ─────────────────────────────────────

  const fetchGatewayStatus = useCallback(async () => {
    try {
      const { data } = await supabase.functions.invoke<GatewayStatus>('ctrader-gateway', {
        method: 'POST',
        body: { action: 'status' },
      });
      if (data) {
        setGatewayConnected(data.connected);
        setGatewayAccounts(data.accounts || []);
      }
    } catch {
      setGatewayConnected(false);
    }
  }, []);

  useEffect(() => {
    if (gatewayPolledRef.current) return;
    gatewayPolledRef.current = true;
    fetchGatewayStatus();
  }, [fetchGatewayStatus]);

  // 🔁 POLL gateway status every 30s so children always have fresh state
  useEffect(() => {
    const timer = setInterval(() => {
      fetchGatewayStatus();
    }, 30000);
    return () => clearInterval(timer);
  }, [fetchGatewayStatus]);

  // Fetch MT4 accounts from user_connections (Dashboard-configured master accounts)
  const fetchMt4Connections = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('user_connections')
        .select('field1, field2')
        .eq('type', 'mt4')
        .order('created_at', { ascending: false });

      if (data) {
        const mt4s = data.map((row: Record<string, unknown>) => ({
          account_id: String(row.field1 || ''),
          account_name: `${row.field1 || '?'} @ ${row.field2 || '?'}`,
          is_live: false,
        }));
        setUserConnectionMt4s(mt4s);
      }
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchMt4Connections();
  }, [fetchMt4Connections]);

  // Realtime: refresh MT4 accounts when admin modifies them in Dashboard
  useEffect(() => {
    const channel = supabase
      .channel('strategies-mt4-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_connections', filter: 'type=eq.mt4' },
        () => {
          console.log('[Strategies] user_connections MT4 changed, refreshing...');
          fetchMt4Connections();
        }
      )
      .subscribe((status) => {
        console.log('[Strategies] Realtime MT4 subscription:', status);
      });

    return () => {
      supabase.removeChannel(channel).catch(() => {});
    };
  }, [fetchMt4Connections]);

  // Auto-select first account
  useEffect(() => {
    if (gatewayAccounts.length > 0 && selectedAccountId === null) {
      setSelectedAccountId(gatewayAccounts[0].account_id);
    }
  }, [gatewayAccounts, selectedAccountId]);

  // Strategy counts per account
  const strategyCounts = useMemo(() => {
    // All connected accounts share the same strategies
    const counts: Record<string, number> = {};
    const total = loadedStrategies.length;
    for (const acc of gatewayAccounts) {
      counts[acc.account_id] = total;
    }
    return counts;
  }, [loadedStrategies, gatewayAccounts]);

  // Collect MT4 accounts from strategies + user_connections (Dashboard)
  const mt4Accounts = useMemo(() => {
    const seen = new Set<string>();
    const accounts: Array<{ account_id: string; account_name: string; is_live: boolean }> = [];

    // From user_connections (Dashboard MT4 Master Accounts) — these appear FIRST
    for (const uc of userConnectionMt4s) {
      if (!seen.has(uc.account_id)) {
        seen.add(uc.account_id);
        accounts.push(uc);
      }
    }

    // From strategies assigned_accounts
    for (const s of loadedStrategies) {
      for (const assigned of s.assigned_accounts) {
        if (assigned.platform !== 'mt4') continue;
        const key = assigned.account_id;
        if (!seen.has(key)) {
          seen.add(key);
          accounts.push({
            account_id: assigned.account_id,
            account_name: assigned.account_name,
            is_live: assigned.is_live || false,
          });
        }
      }
    }
    return accounts;
  }, [loadedStrategies, userConnectionMt4s]);

  const selectedAccount = useMemo(
    () => gatewayAccounts.find((a) => a.account_id === selectedAccountId) || null,
    [gatewayAccounts, selectedAccountId]
  );

  const openPopup = (type: PopupType) => setActivePopup(type);
  const closePopup = () => setActivePopup(null);

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 space-y-5">
      {/* ─── Page Header + Quick Tools ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-500/10 flex items-center justify-center">
            <i className="ri-flashlight-line text-primary-400 text-lg"></i>
          </div>
          <div>
            <h1 className="text-lg font-heading font-bold text-foreground-950">Strategies</h1>
            <p className="text-xs text-foreground-500">Automated trading strategies across your cTrader accounts</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Gateway status */}
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
            gatewayConnected
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
              : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${gatewayConnected ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`}></div>
            {gatewayConnected ? `Gateway · ${gatewayAccounts.length} acc` : 'Offline'}
          </div>

          {/* Debug tool buttons */}
          <div className="flex items-center gap-1 bg-background-100 border border-background-200/70 rounded-full p-1">
            <button
              type="button"
              onClick={() => openPopup('copyTrade')}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-medium text-foreground-500 hover:text-emerald-400 hover:bg-emerald-500/5 transition-all cursor-pointer whitespace-nowrap"
              title="Copy Trade Engine — cTrader → MT4 real-time copy"
            >
              <i className="ri-swap-line text-xs"></i>
              <span className="hidden md:inline">Copy Trade</span>
            </button>
          </div>
        </div>
      </div>

      {/* ─── Account Tabs ─── */}
      <AccountTabs
        accounts={gatewayAccounts}
        selectedAccountId={selectedAccountId}
        strategyCounts={strategyCounts}
        onSelect={setSelectedAccountId}
        isGatewayConnected={gatewayConnected}
      />

      {/* ─── Main Content: Strategies + Sidebar ─── */}
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Left: Strategy Grid */}
        <div className="flex-1 min-w-0">
          <StrategyGrid
            selectedAccount={selectedAccount}
            onStrategiesLoaded={setLoadedStrategies}
          />
        </div>

        {/* Right: Symbol Lists + MT4 Copy Targets */}
        <div className="w-full lg:w-[380px] shrink-0 space-y-4">
          <SymbolListPanel
            selectedCtraderAccountId={selectedAccountId}
            ctraderAccounts={gatewayAccounts}
            strategies={loadedStrategies}
          />

          <CopyTargets
            strategies={loadedStrategies}
            selectedAccountId={selectedAccountId || ''}
            ctraderAccounts={gatewayAccounts}
            userConnectionMt4s={userConnectionMt4s}
          />
        </div>
      </div>

      {/* ─── Popups (debug tools) ─── */}

      {/* Copy Trade Engine Popup */}
      <PopupOverlay
        isOpen={activePopup === 'copyTrade'}
        onClose={closePopup}
        title="Copy Trade Engine"
        subtitle="Real-time cTrader → MT4 trade mirroring — tick by tick"
        icon="ri-swap-line"
        iconBg="bg-emerald-500/10"
        iconColor="text-emerald-400"
      >
        <CopyTradeMonitor
          isCtraderConnected={gatewayConnected}
          ctraderAccounts={gatewayAccounts}
          mt4Accounts={mt4Accounts}
        />
      </PopupOverlay>
    </div>
  );
}